import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, getImageUrl } from "../lib/api";
import { ArrowLeft, Save, Trash2, Plus, Gauge, Activity, Lightbulb, BarChart3, Image as ImageIcon, Eye } from "lucide-react";
import { toast } from "sonner";
const WIDGET_KINDS = [
  { kind: "value", label: "Value Box", icon: Activity },
  { kind: "gauge", label: "Gauge", icon: Gauge },
  { kind: "indicator", label: "Indicator", icon: Lightbulb },
  { kind: "bar", label: "Bar", icon: BarChart3 },
];

function statusColor(s) {
  return s === "critical" ? "#FF3B30" : s === "warning" ? "#FFB300" : "#00E676";
}

export function MimicWidget({ w, tag, onClick, selected }) {
  const color = w.color || (tag ? statusColor(tag.current_status) : "#5A6573");
  const value = tag?.tag_type === "digital" ? (tag.current_value >= 0.5 ? "ON" : "OFF") : tag?.current_value?.toFixed?.(2);
  const label = w.label || tag?.name || "—";
  const scale = w.size || 1;
  const fontLg = 18 * scale; const fontMd = 12 * scale; const fontSm = 10 * scale;

  let content = null;
  if (w.kind === "indicator") {
    content = (
      <div className="flex items-center gap-2 px-2 py-1.5">
        <div className="w-3 h-3 rounded-full" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
        <div>
          <div className="font-mono-metric text-[9px] uppercase text-[#9BA4B5] leading-none">{label}</div>
          <div className="font-mono-metric text-xs text-[#E3E8EE] leading-tight">{value ?? "—"}</div>
        </div>
      </div>
    );
  } else if (w.kind === "gauge") {
    const pct = tag && tag.tag_type === "analog"
      ? Math.max(0, Math.min(100, ((tag.current_value - tag.min_value) / (tag.max_value - tag.min_value)) * 100))
      : 0;
    const dash = (pct / 100) * 175;
    content = (
      <div className="p-2 flex flex-col items-center">
        <svg viewBox="0 0 80 50" width="80" height="50">
          <path d="M10,45 A30,30 0 0,1 70,45" fill="none" stroke="#2C3238" strokeWidth="6" />
          <path d="M10,45 A30,30 0 0,1 70,45" fill="none" stroke={color} strokeWidth="6" strokeDasharray={`${dash} 175`} />
        </svg>
        <div className="font-mono-metric text-xs text-[#E3E8EE] -mt-1">{value ?? "—"} {tag?.unit}</div>
        <div className="font-mono-metric text-[8px] uppercase text-[#9BA4B5]">{label}</div>
      </div>
    );
  } else if (w.kind === "bar") {
    const pct = tag && tag.tag_type === "analog"
      ? Math.max(0, Math.min(100, ((tag.current_value - tag.min_value) / (tag.max_value - tag.min_value)) * 100))
      : 0;
    content = (
      <div className="p-2 w-32">
        <div className="font-mono-metric text-[9px] uppercase text-[#9BA4B5]">{label}</div>
        <div className="h-2 bg-[#0B0D0F] mt-1 border border-[#2C3238]">
          <div className="h-full" style={{ width: `${pct}%`, background: color }} />
        </div>
        <div className="font-mono-metric text-xs text-[#E3E8EE] mt-1">{value ?? "—"} {tag?.unit}</div>
      </div>
    );
  } else {
    // value
    content = (
      <div className="px-3 py-2">
        <div className="font-mono-metric text-[9px] uppercase text-[#9BA4B5] leading-none">{label}</div>
        <div className="font-mono-metric text-lg text-[#E3E8EE] leading-tight" style={{ color }}>{value ?? "—"} <span className="text-[#9BA4B5] text-[10px]">{tag?.unit}</span></div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className={`absolute bg-[#1E2226] border ${selected ? "border-[#00E5FF]" : "border-[#2C3238]"} hover:border-[#00E5FF] cursor-move`}
      style={{ left: w.x, top: w.y, transform: `scale(${scale})`, transformOrigin: "top left" }}
      data-testid={`widget-${w.id}`}
    >
      {content}
    </div>
  );
}

export default function MimicEditor() {
  const { id } = useParams();
  const [mimic, setMimic] = useState(null);
  const [tags, setTags] = useState([]);
  const [selected, setSelected] = useState(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    api.get(`/mimics/${id}`).then(({ data }) => {
      setMimic(data);
      api.get("/tags", { params: { site_id: data.site_id } }).then(r => setTags(r.data));
    });
  }, [id]);

  // Live values refresh
  useEffect(() => {
    if (!mimic) return;
    const iv = setInterval(() => {
      api.get("/tags", { params: { site_id: mimic.site_id } }).then(r => setTags(r.data));
    }, 2000);
    return () => clearInterval(iv);
  }, [mimic?.site_id]);

  const addWidget = (kind) => {
    const newW = {
      id: crypto.randomUUID(),
      kind,
      tag_id: tags[0]?.id || "",
      x: 40, y: 40, label: "",
      size: 1, color: "",
    };
    setMimic({ ...mimic, widgets: [...(mimic.widgets || []), newW] });
    setSelected(newW.id);
  };

  const updateWidget = (wid, patch) => {
    setMimic({ ...mimic, widgets: mimic.widgets.map(w => w.id === wid ? { ...w, ...patch } : w) });
  };
  const removeWidget = (wid) => {
    setMimic({ ...mimic, widgets: mimic.widgets.filter(w => w.id !== wid) });
    setSelected(null);
  };

  const onMouseDown = (e, w) => {
    e.stopPropagation();
    setSelected(w.id);
    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = { wid: w.id, offsetX: e.clientX - rect.left - w.x, offsetY: e.clientY - rect.top - w.y };
  };

  const onMouseMove = useCallback((e) => {
    if (!dragRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, e.clientX - rect.left - dragRef.current.offsetX);
    const y = Math.max(0, e.clientY - rect.top - dragRef.current.offsetY);
    updateWidget(dragRef.current.wid, { x, y });
  }, [mimic]);

  const onMouseUp = () => { dragRef.current = null; };

  const save = async () => {
    try {
      await api.put(`/mimics/${id}`, {
        site_id: mimic.site_id, name: mimic.name, background_url: mimic.background_url, widgets: mimic.widgets,
        bg_opacity: mimic.bg_opacity ?? 0.55,
        bg_width: mimic.bg_width || 0, bg_height: mimic.bg_height || 0,
      });
      toast.success("Mimic saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!mimic) return <div className="text-[#9BA4B5] font-mono-metric">LOADING…</div>;

  const selectedW = mimic.widgets?.find(w => w.id === selected);
  const tagById = Object.fromEntries(tags.map(t => [t.id, t]));

  return (
    <div className="space-y-4" onMouseMove={onMouseMove} onMouseUp={onMouseUp}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/mimics" className="text-[#9BA4B5] hover:text-[#E3E8EE]"><ArrowLeft size={18} /></Link>
          <div>
            <div className="scada-label">MIMIC EDITOR</div>
            <h1 className="font-heading font-black text-2xl tracking-tight">{mimic.name}</h1>
          </div>
        </div>
        <div className="flex gap-1">
          <Link to={`/mimics/view/${id}`} target="_blank" rel="noopener noreferrer" className="border border-[#2C3238] text-[#9BA4B5] hover:text-[#E3E8EE] hover:border-[#E3E8EE] px-3 py-2 font-mono-metric uppercase tracking-wider text-xs flex items-center gap-2"><Eye size={12} /> Preview</Link>
          <button onClick={save} data-testid="save-mimic-btn" className="border border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10 px-4 py-2 font-mono-metric uppercase tracking-wider text-xs flex items-center gap-2"><Save size={12} /> Save</button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-1">
        {/* Toolbox */}
        <div className="col-span-12 md:col-span-4 lg:col-span-2 scada-panel p-3 space-y-3">
          <div className="scada-label">WIDGETS</div>
          <div className="space-y-1">
            {WIDGET_KINDS.map(k => (
              <button
                key={k.kind}
                data-testid={`add-widget-${k.kind}`}
                onClick={() => addWidget(k.kind)}
                className="w-full flex items-center gap-2 border border-[#2C3238] hover:border-[#00E5FF] hover:text-[#00E5FF] text-[#9BA4B5] px-3 py-2 font-mono-metric uppercase text-[10px] tracking-wider"
              >
                <k.icon size={12} /> {k.label}
              </button>
            ))}
          </div>
          <div className="border-t border-[#2C3238] pt-3">
            <div className="scada-label mb-1">BACKGROUND</div>
            <input
              type="text"
              placeholder="image URL"
              value={mimic.background_url || ""}
              onChange={(e) => setMimic({ ...mimic, background_url: e.target.value })}
              className="w-full border bg-[#0B0D0F] px-2 py-1.5 text-[10px] font-mono-metric"
            />
            <label className="mt-2 block cursor-pointer">
              <input type="file" accept="image/*" className="hidden" data-testid="mimic-image-upload" onChange={async (e) => {
                const file = e.target.files?.[0]; if (!file) return;
                if (file.size > 4 * 1024 * 1024) { toast.error("Image > 4MB"); return; }
                const reader = new FileReader();
                reader.onload = async () => {
                  try {
                    const { data } = await api.post("/upload/image", { data_url: reader.result });
                    // Read natural dimensions
                    const img = new window.Image();
                    img.onload = () => {
                      setMimic((m) => ({ ...m, background_url: data.url, bg_width: img.naturalWidth, bg_height: img.naturalHeight }));
                      toast.success(`Image ${img.naturalWidth}×${img.naturalHeight} uploaded`);
                    };
                    img.src = getImageUrl(data.url);
                  } catch (err) { toast.error(formatApiError(err)); }
                };
                reader.readAsDataURL(file);
              }} />
              <div className="w-full border border-[#2C3238] hover:border-[#00E5FF] hover:text-[#00E5FF] text-[#9BA4B5] px-2 py-2 text-center font-mono-metric uppercase text-[10px] tracking-wider">
                UPLOAD IMAGE
              </div>
            </label>
            {mimic.background_url && (
              <>
                <button type="button" onClick={() => setMimic({ ...mimic, background_url: "", bg_width: 0, bg_height: 0 })} className="w-full mt-1 border border-[#2C3238] hover:border-[#FF3B30] hover:text-[#FF3B30] text-[#9BA4B5] px-2 py-1 font-mono-metric uppercase text-[10px] tracking-wider">
                  CLEAR IMAGE
                </button>
                {mimic.bg_width ? <div className="mt-1 font-mono-metric text-[9px] text-[#5A6573]">CANVAS: {mimic.bg_width}×{mimic.bg_height}px</div> : null}
              </>
            )}
            <div className="scada-label mt-3 mb-1">IMAGE OPACITY</div>
            <input type="range" min="0.1" max="1" step="0.05" value={mimic.bg_opacity ?? 0.55}
              onChange={(e) => setMimic({ ...mimic, bg_opacity: parseFloat(e.target.value) })}
              className="w-full" />
          </div>
        </div>

        {/* Canvas */}
        <div className="col-span-12 md:col-span-8 lg:col-span-8">
          <div className="w-full max-h-[75vh] overflow-auto border border-[#2C3238]">
            <div
              ref={canvasRef}
              data-testid="mimic-canvas"
              onClick={() => setSelected(null)}
              className="relative dot-grid"
              style={{
                width: mimic.bg_width ? `${mimic.bg_width}px` : "1600px",
                height: mimic.bg_height ? `${mimic.bg_height}px` : "900px",
                backgroundColor: "#0B0D0F",
                backgroundImage: mimic.background_url
                  ? `linear-gradient(rgba(11,13,15,${mimic.bg_opacity ?? 0.55}), rgba(11,13,15,${mimic.bg_opacity ?? 0.55})), url(${getImageUrl(mimic.background_url)})`
                  : "none",
                backgroundRepeat: "no-repeat",
                backgroundSize: mimic.bg_width ? `${mimic.bg_width}px ${mimic.bg_height}px` : "cover",
                backgroundPosition: "top left",
              }}
            >
              {(mimic.widgets || []).map(w => (
                <div key={w.id} onMouseDown={(e) => onMouseDown(e, w)}>
                  <MimicWidget w={w} tag={tagById[w.tag_id]} onClick={(e) => { e.stopPropagation(); setSelected(w.id); }} selected={selected === w.id} />
                </div>
              ))}
            </div>
          </div>
          <div className="font-mono-metric text-[10px] text-[#5A6573] mt-2 uppercase tracking-widest">
            DRAG WIDGETS TO POSITION • {mimic.widgets?.length || 0} ITEMS
            {mimic.bg_width ? ` • CANVAS ${mimic.bg_width}×${mimic.bg_height}` : ""}
          </div>
        </div>

        {/* Properties */}
        <div className="col-span-12 md:col-span-12 lg:col-span-2 scada-panel p-3">
          <div className="scada-label mb-2">PROPERTIES</div>
          {!selectedW ? (
            <div className="text-[#5A6573] text-xs font-mono-metric">Select a widget</div>
          ) : (
            <div className="space-y-3">
              <div>
                <div className="scada-label mb-1">TAG</div>
                <select value={selectedW.tag_id} onChange={(e) => updateWidget(selectedW.id, { tag_id: e.target.value })} className="w-full border bg-[#0B0D0F] px-2 py-1.5 text-[10px] font-mono-metric">
                  {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <div className="scada-label mb-1">KIND</div>
                <select value={selectedW.kind} onChange={(e) => updateWidget(selectedW.id, { kind: e.target.value })} className="w-full border bg-[#0B0D0F] px-2 py-1.5 text-[10px] font-mono-metric">
                  {WIDGET_KINDS.map(k => <option key={k.kind} value={k.kind}>{k.label}</option>)}
                </select>
              </div>
              <div>
                <div className="scada-label mb-1">LABEL</div>
                <input value={selectedW.label || ""} onChange={(e) => updateWidget(selectedW.id, { label: e.target.value })} className="w-full border bg-[#0B0D0F] px-2 py-1.5 text-[10px] font-mono-metric" placeholder="(uses tag name)" />
              </div>
              <div className="grid grid-cols-2 gap-1">
                <div>
                  <div className="scada-label mb-1">X</div>
                  <input type="number" value={Math.round(selectedW.x)} onChange={(e) => updateWidget(selectedW.id, { x: parseInt(e.target.value) || 0 })} className="w-full border bg-[#0B0D0F] px-2 py-1.5 text-[10px] font-mono-metric" />
                </div>
                <div>
                  <div className="scada-label mb-1">Y</div>
                  <input type="number" value={Math.round(selectedW.y)} onChange={(e) => updateWidget(selectedW.id, { y: parseInt(e.target.value) || 0 })} className="w-full border bg-[#0B0D0F] px-2 py-1.5 text-[10px] font-mono-metric" />
                </div>
              </div>
              <div>
                <div className="scada-label mb-1">SIZE ({(selectedW.size || 1).toFixed(2)}×)</div>
                <input type="range" min="0.5" max="3" step="0.05" value={selectedW.size || 1}
                  onChange={(e) => updateWidget(selectedW.id, { size: parseFloat(e.target.value) })}
                  className="w-full" data-testid="widget-size" />
              </div>
              <div>
                <div className="scada-label mb-1">COLOR OVERRIDE</div>
                <div className="flex gap-1 flex-wrap">
                  {["", "#00E5FF", "#00E676", "#FFB300", "#FF3B30", "#B388FF", "#FFFFFF"].map(c => (
                    <button key={c || "auto"} type="button" onClick={() => updateWidget(selectedW.id, { color: c })}
                      title={c || "Auto (status)"} data-testid={`color-${c || "auto"}`}
                      className={`w-5 h-5 border ${selectedW.color === c ? "border-[#00E5FF]" : "border-[#2C3238]"}`}
                      style={{ background: c || "transparent", backgroundImage: c ? undefined : "linear-gradient(45deg,#333 25%,transparent 25%,transparent 75%,#333 75%),linear-gradient(45deg,#333 25%,transparent 25%,transparent 75%,#333 75%)", backgroundSize: "6px 6px" }} />
                  ))}
                </div>
              </div>
              <button data-testid={`remove-widget-${selectedW.id}`} onClick={() => removeWidget(selectedW.id)} className="w-full border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 px-2 py-1.5 font-mono-metric uppercase tracking-wider text-[10px] flex items-center justify-center gap-1">
                <Trash2 size={10} /> Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
